import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Search, 
  CheckCircle, 
  AlertCircle, 
  XCircle,
  RefreshCw,
  TrendingUp,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

interface WebsiteData {
  id: string;
  domain: string;
  title: string;
  favicon_url: string;
}

interface SeoAuditTabProps {
  website: WebsiteData;
  isActive?: boolean;
}

interface SeoAudit {
  id: string;
  score: number;
  issues: any;
  recommendations: any;
  audit_data: any;
  created_at: string;
  website_id: string;
}

export function SeoAuditTab({ website, isActive = true }: SeoAuditTabProps) {
  const [seoAudits, setSeoAudits] = useState<SeoAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Track if data has been loaded initially
  const [loadedWebsiteId, setLoadedWebsiteId] = useState<string | null>(null);

  useEffect(() => {
    // Only load data if tab is active
    if (!isActive) return;
    
    // Reset loaded state if website changed
    if (website?.id !== loadedWebsiteId) {
      setLoadedWebsiteId(null);
    }
    
    if (website?.id && website.id !== loadedWebsiteId) {
      loadSeoAudits();
      setLoadedWebsiteId(website.id);
    }
  }, [website?.id, isActive, loadedWebsiteId]);

  const loadSeoAudits = async () => {
    try {
      const { data, error } = await supabase
        .from('seo_audits')
        .select('*')
        .eq('website_id', website.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setSeoAudits(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading SEO audits:', error);
      toast.error("Error loading SEO audits");
      setLoading(false);
    }
  };

  const runNewAudit = async () => {
    setAnalyzing(true);
    try {
      const { data, error } = await supabase.functions.invoke('analyze-website', {
        body: {
          websiteUrl: `https://${website.domain}`,
          websiteId: website.id
        }
      });

      if (error) throw error;
      if (data.success) {
        toast.success("SEO audit completed!");
        await loadSeoAudits();
      }
    } catch (error) {
      console.error('Error running SEO audit:', error);
      toast.error("Failed to run SEO audit");
    } finally {
      setAnalyzing(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getIssueIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'medium':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'low':
        return <AlertTriangle className="h-4 w-4 text-blue-500" />;
      default:
        return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
  };

  const mockIssues = [
    {
      id: "1",
      type: "Missing meta description",
      severity: "medium",
      description: "12 pages are missing meta descriptions",
      recommendation: "Add unique meta descriptions for better search engine visibility"
    },
    {
      id: "2",
      type: "Slow page load speed",
      severity: "high", 
      description: "Average page load time is 4.2 seconds",
      recommendation: "Optimize images and minify CSS/JS files"
    },
    {
      id: "3",
      type: "Missing alt tags",
      severity: "medium",
      description: "8 images are missing alt attributes",
      recommendation: "Add descriptive alt text to all images"
    }
  ];

  if (loading) {
    return (
      <div className="p-6 space-y-8">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const latestAudit = seoAudits[0];

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-foreground">SEO Audit</h2>
          <p className="text-muted-foreground">Comprehensive analysis of your website's SEO performance</p>
        </div>
        <Button 
          onClick={runNewAudit} 
          disabled={analyzing}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${analyzing ? 'animate-spin' : ''}`} />
          {analyzing ? 'Analyzing...' : 'Run New Audit'}
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overall SEO Score</CardTitle>
            <Search className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${latestAudit ? getScoreColor(latestAudit.score) : 'text-muted-foreground'}`}>
              {latestAudit ? `${latestAudit.score}/100` : 'No data'}
            </div>
            {latestAudit && (
              <Progress 
                value={latestAudit.score} 
                className="mt-2"
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Issues Found</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{mockIssues.length}</div>
            <div className="flex gap-2 mt-2">
              <Badge variant="destructive" className="text-xs">2 High</Badge>
              <Badge variant="secondary" className="text-xs">1 Medium</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Audit</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold">
              {latestAudit ? new Date(latestAudit.created_at).toLocaleDateString() : 'Never'}
            </div>
            <p className="text-xs text-muted-foreground">
              {latestAudit ? `${Math.floor((Date.now() - new Date(latestAudit.created_at).getTime()) / (1000 * 60 * 60 * 24))} days ago` : 'Run your first audit'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Issues List */}
      <Card>
        <CardHeader>
          <CardTitle>SEO Issues & Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          {latestAudit ? (
            <div className="space-y-4">
              {mockIssues.map((issue) => (
                <div key={issue.id} className="flex items-start gap-4 p-4 border border-border rounded-lg">
                  {getIssueIcon(issue.severity)}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-foreground">{issue.type}</h4>
                      <Badge 
                        variant={issue.severity === 'high' ? 'destructive' : 'secondary'}
                        className="text-xs"
                      >
                        {issue.severity}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{issue.description}</p>
                    <div className="bg-muted/50 p-3 rounded-md">
                      <p className="text-xs font-medium text-foreground mb-1">Recommendation:</p>
                      <p className="text-xs text-muted-foreground">{issue.recommendation}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No audit data available</h3>
              <p className="text-muted-foreground mb-4">Run your first SEO audit to identify optimization opportunities</p>
              <Button onClick={runNewAudit} disabled={analyzing}>
                {analyzing ? 'Analyzing...' : 'Run First Audit'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit History */}
      {seoAudits.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Audit History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {seoAudits.slice(1).map((audit) => (
                <div key={audit.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div>
                    <p className="font-medium text-foreground">
                      Score: {audit.score}/100
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(audit.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant="outline">
                    {audit.issues ? Object.keys(audit.issues).length : 0} issues
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}